import type { ReactNode } from 'react'

import { cn } from '../../_lib/utils'

export interface SegmentedControlOption<T extends string | number> {
  value: T
  label: ReactNode
  disabled?: boolean
}

export interface SegmentedControlProps<T extends string | number> {
  options: SegmentedControlOption<T>[]
  value: T | null | undefined
  onChange: (value: T) => void
  disabled?: boolean
  fullWidth?: boolean
  className?: string
}

export const SegmentedControl = <T extends string | number>({
  options,
  value,
  onChange,
  disabled = false,
  fullWidth = false,
  className,
}: SegmentedControlProps<T>) => {
  return (
    <div
      className={cn(
        'inline-flex rounded-full bg-white p-1 shadow-sm',
        fullWidth && 'flex w-full',
        className,
      )}
    >
      {options.map((option) => {
        const isSelected = value === option.value
        const isDisabled = disabled || option.disabled

        return (
          <button
            key={option.value}
            type="button"
            disabled={isDisabled}
            onClick={() => onChange(option.value)}
            className={cn(
              'rounded-full px-5 py-2.5 text-sm font-bold transition-all',
              fullWidth && 'flex-1',
              isSelected
                ? 'bg-brand-green-primary-5 text-white shadow-sm'
                : isDisabled
                  ? 'text-brand-grayscale-5'
                  : 'text-brand-green-primary-5 hover:bg-brand-green-primary-1',
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
